import React, { useState, useEffect, useRef } from 'react';
import menuDescriptions from './menuDescriptions';
import tabStyles from './MenuTabs.module.css';
import { 
  ChevronDown, 
  ChevronUp, 
  Info, 
  HelpCircle, 
  User, 
  Mail, 
  Phone, 
  Briefcase, 
  Book, 
  Shield,
  Star,
  Award,
  Crown,
  Heart,
  CircleUser,
  Settings,
  AlertCircle
} from 'lucide-react';

const HeaderMenu = ({ menuOpen, setMenuOpen }) => {  
  const [activeTab, setActiveTab] = useState(null);
  const menuRef = useRef(null);
  const tabContentRef = useRef(null);
  
  // Get icon for menu item - customize based on your menu items
  const getIconForItem = (id) => {
    const iconSize = 20;
    
    switch(id) {
      case 'about':
        return <Info size={iconSize} />;
      case 'services':
        return <Briefcase size={iconSize} />;
      case 'contact':
        return <Mail size={iconSize} />;
      case 'faq':
        return <HelpCircle size={iconSize} />;
      case 'privacy':
        return <Shield size={iconSize} />;
      case 'terms':
        return <Book size={iconSize} />;
      case 'team':
        return <User size={iconSize} />;
      case 'support':
        return <Phone size={iconSize} />;
      case 'features':
        return <Star size={iconSize} />;
      case 'tier':
      case 'pro':
      case 'pricing':
        return <Crown size={iconSize} />;
      case 'benefits':
        return <Award size={iconSize} />;
      case 'account':
        return <CircleUser size={iconSize} />;
      case 'testimonials':
        return <Heart size={iconSize} />;
      case 'settings':
        return <Settings size={iconSize} />;
      case 'warning':
      case 'notice':
        return <AlertCircle size={iconSize} />;
      default:
        return <Info size={iconSize} />; // Default icon
    }
  };

  useEffect(() => {
    if (menuOpen) {
      const hash = window.location.hash;
      if (hash) {
        const tabId = hash.replace('#', '');
        const validTab = menuDescriptions.find(item => item.id === tabId);
        if (validTab) {
          setActiveTab(tabId);
          return;
        }
      }
      setActiveTab(null);
    } else {
      setActiveTab(null);
      window.history.pushState(null, '', window.location.pathname);
    }
  }, [menuOpen]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, setMenuOpen]);

  // Effect to handle tab content scrolling
  useEffect(() => {
    if (activeTab && tabContentRef.current) {
      // Ensure the tab content is scrollable
      setTimeout(() => {
        const tabElement = tabContentRef.current;
        if (tabElement) {
          // Reset scroll position
          tabElement.scrollTop = 0;
          
          // Force scrollable style
          tabElement.style.overflowY = 'auto';
          tabElement.style.maxHeight = window.innerWidth <= 768 ? '300px' : '400px';
          
          // Ensure content is visible on mobile
          if (window.innerWidth <= 768) {
            const menuElement = menuRef.current;
            if (menuElement) {
              const itemTop = tabElement.parentElement.offsetTop;
              menuElement.scrollTop = itemTop - 60;
            }
          }
        }
      }, 50);
    }
  }, [activeTab]);

  const handleClick = (id, e) => {
    e.preventDefault();
    setActiveTab(activeTab === id ? null : id);
    
    // Scroll to the top of the menu when changing tabs
    if (menuRef.current) {
      menuRef.current.scrollTop = 0;
    }
  };

  return menuOpen ? (
    <div ref={menuRef} className={tabStyles.menuContainer}>
      <ul className={tabStyles.menuList}>
        {menuDescriptions.map(item => (
          <li 
            key={item.id}
            className={`${tabStyles.menuItem} ${activeTab === item.id ? tabStyles.active : ''}`}
            onClick={(e) => handleClick(item.id, e)}
          >
            <div className={tabStyles.menuItemHeader}>
              <div className={tabStyles.menuItemTitle}>
                {getIconForItem(item.id)}
                <span>{item.title}</span>
              </div>
              {activeTab === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            
            {activeTab === item.id && (
              <div 
                ref={activeTab === item.id ? tabContentRef : null}
                className={tabStyles.tabContent}
              >
                <h2 className={tabStyles.title}>{item.title}</h2>
                <div
                  className={tabStyles.description}
                  dangerouslySetInnerHTML={{
                    __html: item.description.replace(/\n/g, '<br>'),
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  ) : null;
};

export default HeaderMenu;